import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import { requireAuthenticatedUser } from '../../decorators'
import {
  getLocationListData,
  getLocationDataById,
  getLocationOverviewDataForId,
  getCompanyDataByLocationId,
} from './mockData'
import type { ILocation, ILocationOverview } from '~/types/location/models'
import type { ICompany } from '~/types/company/models'

interface GetLocationResponse {
  data: ILocation
}

const getLocationById = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetLocationResponse = {
      data: await getLocationDataById(id),
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    const responseBody = {
      statusCode: 404,
      message: `Unable to retrieve location '${id}'`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetLocationOverviewResponse {
  data: ILocationOverview
}

const getLocationOverviewById = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetLocationOverviewResponse = {
      data: await getLocationOverviewDataForId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    const responseBody = {
      statusCode: 404,
      message: `Unable to retrieve location overview for '${id}'`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetCompanyByLocationIdResponse {
  data: ICompany
}

const getCompanyByLocationId = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyByLocationIdResponse = {
      data: await getCompanyDataByLocationId(id)!, // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e) {
    const responseBody = {
      statusCode: 404,
      message: `Unable to retrieve company for locationId '${id}'`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

// post

const postLocation = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(req, res, ctx, getLocationListData)
}

const patchLocation = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getLocationListData)
}

const deleteLocation = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(req, res, ctx, getLocationListData)
}

export const handlers = [
  {
    method: 'get',
    path: '/api/locations/:id',
    handler: getLocationById,
  },
  {
    method: 'get',
    path: '/api/locations/:id/overview',
    handler: getLocationOverviewById,
  },
  {
    method: 'get',
    path: '/api/locations/:id/company',
    handler: getCompanyByLocationId,
  },
  {
    method: 'post',
    path: '/api/locations',
    handler: postLocation,
  },
  {
    method: 'patch',
    path: '/api/locations/:id',
    handler: patchLocation,
  },
  {
    method: 'delete',
    path: '/api/locations/:id',
    handler: deleteLocation,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
